import type { FormIdentifier, Project, Stored, User } from "@overmap-ai/common"
import { FieldSchema } from "@overmap-ai/forms"

import type { FormSubmissionData } from "../../typings"
import { CreatedByFilterDef } from "../defs/CreatedByFilterDef"
import { ProjectFilterDef } from "../defs/ProjectFilterDef"
import { SubmittedAtFilterDef } from "../defs/SubmittedAtFilterDef"
import { getFilterDefConfigsForFieldSchema, getFilterDefsOfIdentifiers } from "../defs/utils"
import { FilterSchema } from "./FilterSchema"
import { createFilterDefConfig } from "./utils"

interface FormSubmissionFilterSchemaConfig {
	formIdentifiers: Stored<FormIdentifier>[]
	fieldSchema: FieldSchema
	users: User[]
	projects?: Project[]
}

export const getFormSubmissionTableFilterSchema = (config: FormSubmissionFilterSchemaConfig) => {
	const { formIdentifiers, fieldSchema, users, projects } = config

	return FilterSchema.create<FormSubmissionData>({
		filterDefs: [
			createFilterDefConfig({
				filterDef: new CreatedByFilterDef(users),
				accessorFn: ({ formSubmission }) => formSubmission.created_by ?? -1,
			}),
			createFilterDefConfig({
				filterDef: new SubmittedAtFilterDef(),
				accessorFn: ({ formSubmission }) => new Date(formSubmission.submitted_at),
			}),
			...(projects
				? [
						createFilterDefConfig<FormSubmissionData, ProjectFilterDef>({
							filterDef: new ProjectFilterDef(projects),
							accessorFn: ({ formSubmission }) => formSubmission.project ?? "",
						}),
					]
				: []),
			...getFilterDefsOfIdentifiers<Stored<FormIdentifier>, FormSubmissionData>(
				formIdentifiers,
				(identifier) =>
					({ formIdentifierValues }) =>
						formIdentifierValues[identifier.uuid]?.value ?? null,
			),
			...getFilterDefConfigsForFieldSchema<FormSubmissionData>(fieldSchema, (data) => [
				fieldSchema.deserializeValues(data.formSubmissionFieldValues?.values ?? {}),
				data.formSubmissionFieldValuesAttachments,
			]),
		],
	})
}
